import type { McpServer, SharedBot, SharedBotInput } from "@rakazo/contracts";
import { Button, Input, Switch, Textarea } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SharedBotAppearance } from "../components/SharedBotAppearance";
import { SharedBotConnections } from "../components/SharedBotConnections";
import { rpc } from "../lib/rpc";

const blank: SharedBotInput = {
  name: "",
  instructions: "",
  allowedRepos: [],
  readOnly: true as const,
  enabled: false,
  hourlyLimit: 30,
};
export function SharedBotsPage() {
  const [params] = useSearchParams();
  const [servers, setServers] = useState<McpServer[]>([]);
  const [bots, setBots] = useState<SharedBot[]>([]);
  const [editing, setEditing] = useState<(Partial<SharedBot> & SharedBotInput) | null>(
    params.has("new") ? { ...blank } : null,
  );
  const [requirePassword, setRequirePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const refresh = () => rpc.sharedBots.list().then(setBots);
  useEffect(() => {
    void rpc.mcp.servers
      .list()
      .then(setServers)
      .catch(() => setError("Could not load connections."));
    void rpc.sharedBots
      .list()
      .then((rows) => {
        setBots(rows);
        const selected = rows.find((bot) => bot.id === params.get("bot"));
        if (selected) open(selected);
      })
      .catch(() => setError("Could not load shared bots."));
  }, []);
  const open = (bot?: SharedBot) => {
    setEditing(bot ?? { ...blank });
    setRequirePassword(Boolean(bot?.hasPassword));
    setPassword("");
    setError("");
    setNotice("");
  };
  const act = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch {
      setError("Could not save. Check the selected sources and try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="shared-bots-page h-full overflow-auto bg-background text-foreground">
      <div className="min-h-full">
        <Link data-app-home to="/app" className="m-4 inline-block text-sm text-muted-foreground">
          ← Command Center
        </Link>
        {(!editing || editing.id) && (
          <div className="flex min-h-14 items-center justify-end gap-4 bg-card px-5 py-3">
            <h1 data-app-title className="mr-auto text-base font-semibold">
              Shared bots
            </h1>
            <Button onClick={() => open()}>New shared bot</Button>
          </div>
        )}
        {error && (
          <p role="alert" className="mb-4 text-destructive">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="mb-4 text-muted-foreground">
            {notice}
          </p>
        )}
        <div className={`shared-bots-layout${bots.length ? "" : " shared-bots-empty"}`}>
          <aside className="space-y-1 bg-sidebar p-3">
            {bots.map((bot) => (
              <button
                key={bot.id}
                type="button"
                onClick={() => open(bot)}
                className={`w-full rounded-md px-3 py-3 text-left transition-colors hover:bg-muted ${editing?.id === bot.id ? "bg-muted" : ""}`}
              >
                <div className="font-medium">{bot.name}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {bot.enabled ? "Link active" : "Link disabled"} ·{" "}
                  {bot.readOnly ? "Read-only" : "Read & write"}
                </div>
              </button>
            ))}
            {!bots.length && !editing && (
              <p className="text-sm text-muted-foreground">Create a bot to share with your team.</p>
            )}
          </aside>
          {editing ? (
            <form
              className="min-w-0 space-y-5 p-5"
              onSubmit={(event) => {
                event.preventDefault();
                void act(async () => {
                  const saved = await rpc.sharedBots.save({
                    ...editing,
                    password: requirePassword ? password || undefined : null,
                  });
                  open(saved);
                  await refresh();
                  setNotice("Saved");
                });
              }}
            >
              <h2 className="text-lg font-semibold">
                {editing.id ? "Bot settings" : "New shared bot"}
              </h2>
              <label htmlFor="shared-name" className="block text-sm">
                Name
                <Input
                  id="shared-name"
                  className="mt-2"
                  required
                  maxLength={80}
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </label>
              <SharedBotAppearance
                color={editing.color}
                onChange={(color) => setEditing({ ...editing, color })}
              />
              <label htmlFor="shared-slug" className="block text-sm">
                Short link
                <Input
                  id="shared-slug"
                  className="mt-2"
                  maxLength={80}
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  placeholder="design-sidekick"
                  value={editing.slug ?? ""}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value || null })}
                />
                {editing.slug && (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Requires a password.
                  </span>
                )}
              </label>
              <label htmlFor="shared-greeting" className="block text-sm">
                Greeting
                <Input
                  id="shared-greeting"
                  className="mt-2"
                  maxLength={300}
                  placeholder="How can I help you today?"
                  value={editing.greeting ?? ""}
                  onChange={(e) => setEditing({ ...editing, greeting: e.target.value || null })}
                />
              </label>
              <label htmlFor="shared-rules" className="block text-sm">
                Rules
                <Textarea
                  id="shared-rules"
                  className="mt-2"
                  rows={7}
                  maxLength={16000}
                  placeholder="How should this bot answer? What should it focus on or avoid?"
                  value={editing.instructions}
                  onChange={(e) => setEditing({ ...editing, instructions: e.target.value })}
                />
              </label>
              <SharedBotConnections
                key={editing.id ?? "new"}
                servers={servers}
                repos={editing.allowedRepos}
                onReposChange={(allowedRepos) => setEditing({ ...editing, allowedRepos })}
                value={editing.integrations ?? []}
                onChange={(integrations) => setEditing({ ...editing, integrations })}
              />
              <div className="space-y-2 text-sm">
                <label
                  htmlFor="shared-write"
                  className="flex items-center justify-between gap-3 font-medium"
                >
                  Read & write
                  <Switch
                    id="shared-write"
                    checked={!editing.readOnly}
                    onCheckedChange={(enabled) => setEditing({ ...editing, readOnly: !enabled })}
                  />
                </label>
                <p className="mt-2 text-muted-foreground">
                  {editing.readOnly
                    ? "Read-only when off. Can query only the repositories and connected projects selected above."
                    : "Anyone with this link can request supported changes in the selected projects. Your rules still apply."}
                </p>
                {!editing.readOnly && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Supports Linear tickets and comments, and Sentry issue status. Repository code
                    and PostHog analytics remain read-only.
                  </p>
                )}
              </div>
              <label
                htmlFor="shared-limit"
                className="flex items-center justify-between gap-3 text-sm"
              >
                Hourly message limit
                <Input
                  id="shared-limit"
                  type="number"
                  min={1}
                  max={200}
                  className="w-24"
                  value={editing.hourlyLimit}
                  onChange={(e) => setEditing({ ...editing, hourlyLimit: Number(e.target.value) })}
                />
              </label>
              <label
                htmlFor="shared-enabled"
                className="flex items-center justify-between gap-3 text-sm"
              >
                Enable shared link
                <Switch
                  id="shared-enabled"
                  checked={editing.enabled}
                  onCheckedChange={(enabled) => setEditing({ ...editing, enabled })}
                />
              </label>
              <label
                htmlFor="require-password"
                className="flex items-center justify-between gap-3 text-sm"
              >
                Require a password
                <Switch
                  id="require-password"
                  checked={requirePassword}
                  onCheckedChange={setRequirePassword}
                />
              </label>
              {requirePassword && (
                <label htmlFor="shared-password" className="block text-sm">
                  {editing.hasPassword ? "Change password" : "Password"}
                  <Input
                    id="shared-password"
                    type="password"
                    autoComplete="new-password"
                    className="mt-2"
                    value={password}
                    minLength={8}
                    maxLength={128}
                    required={!editing.hasPassword}
                    placeholder={
                      editing.hasPassword
                        ? "Leave blank to keep current password"
                        : "At least 8 characters"
                    }
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </label>
              )}
              <p className="text-xs text-muted-foreground">
                Visitors need the link and, when enabled, the password. No account required. Uses
                your connected models.
              </p>
              {editing.shareUrl && (
                <div className="space-y-2">
                  <label className="block text-sm" htmlFor="shared-link">
                    Web address
                  </label>
                  <Input id="shared-link" readOnly value={editing.shareUrl} />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        void navigator.clipboard.writeText(editing.shareUrl!).then(
                          () => setNotice("Link copied"),
                          () => setError("Select and copy the web address above."),
                        );
                      }}
                    >
                      Copy link
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => window.open(editing.shareUrl, "_blank", "noopener,noreferrer")}
                    >
                      Open chat
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        if (
                          window.confirm(
                            "Replace this link? The old link will stop working immediately.",
                          )
                        )
                          void act(async () => {
                            open(await rpc.sharedBots.rotate({ id: editing.id! }));
                            await refresh();
                          });
                      }}
                    >
                      Replace link
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <Button
                  type="submit"
                  disabled={
                    busy ||
                    !editing.name.trim() ||
                    (requirePassword &&
                      (!editing.hasPassword || password.length > 0) &&
                      password.length < 8)
                  }
                >
                  {busy ? "Saving…" : "Save bot"}
                </Button>
                {editing.id && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm("Delete this shared bot and disable its link?"))
                        void act(async () => {
                          await rpc.sharedBots.remove({ id: editing.id! });
                          setEditing(null);
                          await refresh();
                        });
                    }}
                  >
                    Delete bot
                  </Button>
                )}
              </div>
            </form>
          ) : bots.length ? (
            <div className="p-5 text-sm text-muted-foreground">
              Select a shared bot to edit its rules and link.
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}
